import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { getActiveBusinessId, setActiveBusinessId } from "./tokenStore";
import { isGroupOwner } from "./permissions";
import { PageLoading } from "../components";

/* Routes that work without a business open. Everything else reads business
   data, so it needs one picked first — the group overview is where that
   happens, and it must be exempt or the redirect loops. */
const BUSINESS_OPTIONAL = ["/overview"];

const needsBusiness = (pathname) =>
  !BUSINESS_OPTIONAL.some(
    (p) => pathname === p || pathname.indexOf(`${p}/`) === 0
  );

/* The business a non-owner lands in: the one their session is bound to,
   else their first membership. */
function homeBusinessId(business, memberships) {
  if (business && business.id) return String(business.id);
  const first = (memberships || [])[0];
  return first ? String(first.business_id) : null;
}

export default function ProtectedRoute() {
  const { user, loading, business, memberships } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  // Only owners get the group overview. Everyone else is put straight into
  // their business and sent to its dashboard. With no business to open,
  // fall through to the overview rather than redirect in a loop.
  if (!isGroupOwner(user, memberships)) {
    const home = getActiveBusinessId() || homeBusinessId(business, memberships);
    if (home) {
      if (!getActiveBusinessId()) setActiveBusinessId(home);
      if (!needsBusiness(location.pathname)) return <Navigate to="/" replace />;
    }
  }

  // Signed in but no business open — send them to the overview to pick one
  // rather than rendering a page whose every request would be unscoped.
  if (needsBusiness(location.pathname) && !getActiveBusinessId()) {
    return <Navigate to="/overview" replace />;
  }

  return <Outlet />;
}
